import type { components } from "./api/api.types";
import type { WebSocketEvent, WebSocketTurnStart } from "./websocket/websocket.utils";

/** Max number of files (including a voice note) the client sends on one turn.start. */
export const ATTACHMENT_MAX_PER_MESSAGE = 3;

export type UploadRestrictions = components["schemas"]["UploadRestrictionsDTO"];

// chat attachments cover these backend categories; videos stay out until chat supports them
const CHAT_ATTACHMENT_CATEGORIES = ["documents", "images", "audio"] as const;

export type AttachmentStatus =
  | "validating"
  | "uploading"
  | "pending"
  | "processing"
  | "ready"
  | "failed";

export type AttachmentCategory = "document" | "image" | "audio";

export type AttachmentErrorCode =
  | "ATTACHMENT_NOT_FOUND"
  | "ATTACHMENT_FAILED"
  | "ATTACHMENT_TIMEOUT"
  | "ATTACHMENT_UNAVAILABLE"
  | "ATTACHMENT_LOOKUP_FAILED";

export type Attachment = components["schemas"]["AttachmentDTO"];

export type TurnStartFileItem = Extract<
  WebSocketTurnStart["content"][number],
  { type: "file" }
>;

export type AttachmentRealtimeEvent = WebSocketEvent & {
  upload_id?: string;
  code?: string | null;
  message?: string | null;
};

export type FileLike = {
  type: string;
  size: number;
};

/** chatAttachmentMimeTypes flattens the backend's chat-relevant categories into one allow-list. */
export function chatAttachmentMimeTypes(
  restrictions: UploadRestrictions | undefined,
): string[] {
  const fileTypes = restrictions?.file_types;
  if (!fileTypes) return [];
  return CHAT_ATTACHMENT_CATEGORIES.flatMap(
    (category) => fileTypes[category]?.mime_types ?? [],
  );
}

/** chatAttachmentExtensions flattens the backend's chat-relevant categories into one extension list. */
export function chatAttachmentExtensions(
  restrictions: UploadRestrictions | undefined,
): string[] {
  const fileTypes = restrictions?.file_types;
  if (!fileTypes) return [];
  return CHAT_ATTACHMENT_CATEGORIES.flatMap(
    (category) => fileTypes[category]?.extensions ?? [],
  );
}

/** isMimeAllowed reports whether chat attachments accept the mime type per backend restrictions. */
export function isMimeAllowed(
  mime: string,
  restrictions: UploadRestrictions | undefined,
) {
  return chatAttachmentMimeTypes(restrictions).includes(mime);
}

// extension fallbacks for when the browser reports no mime type (network
// shares, archive viewers, missing OS registrations). zip stays absent on
// purpose — chat attachments do not accept archives.
const EXTENSION_MIME_FALLBACKS: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  json: "application/json",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  mp3: "audio/mpeg",
  m4a: "audio/mp4",
  wav: "audio/wav",
  ogg: "audio/ogg",
  flac: "audio/flac",
};

/** normalizeMimeType strips mime parameters ("text/plain; charset=utf-8") and maps an empty type to application/octet-stream. */
export function normalizeMimeType(mimeType: string) {
  return mimeType.split(";")[0]?.trim() || "application/octet-stream";
}

/** resolveAttachmentMimeType returns the browser-reported mime type, falling back to the file extension when the browser reports none. */
export function resolveAttachmentMimeType(file: { name: string; type: string }) {
  const normalizedType = normalizeMimeType(file.type || "application/octet-stream");
  if (normalizedType !== "application/octet-stream") return normalizedType;

  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return EXTENSION_MIME_FALLBACKS[extension] ?? normalizedType;
}

export type AttachmentValidationReason =
  | "unsupported-type"
  | "file-too-large"
  | "restrictions-loading";

/**
 * validateFile checks a chat attachment against the backend upload
 * restrictions: the mime type must belong to a chat-relevant category and the
 * size must fit both that category's cap and the global upload cap.
 */
export function validateFile(
  file: FileLike,
  restrictions: UploadRestrictions | undefined,
): { valid: true } | { valid: false; reason: AttachmentValidationReason } {
  if (!restrictions) {
    return { valid: false, reason: "restrictions-loading" };
  }

  const category = CHAT_ATTACHMENT_CATEGORIES.map(
    (name) => restrictions.file_types?.[name],
  ).find((candidate) => (candidate?.mime_types ?? []).includes(file.type));

  if (!category) {
    return { valid: false, reason: "unsupported-type" };
  }

  const maxBytes = Math.min(
    category.max_bytes ?? Number.POSITIVE_INFINITY,
    restrictions.max_upload_bytes ?? Number.POSITIVE_INFINITY,
  );
  if (file.size > maxBytes) {
    return { valid: false, reason: "file-too-large" };
  }

  return { valid: true };
}

export function fileSizeLabel(sizeBytes: number) {
  if (sizeBytes <= 0) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(sizeBytes) / Math.log(1024)), 3);
  const size = sizeBytes / 1024 ** exponent;
  return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function categoriseAttachment(mime: string): AttachmentCategory {
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("image/")) return "image";
  return "document";
}

export function mapApiAttachmentStatus(status?: string | null): AttachmentStatus {
  switch (status) {
    case "completed":
      return "ready";
    case "processing":
      return "processing";
    case "pending":
      return "pending";
    case "failed":
      return "failed";
    default:
      return "pending";
  }
}
